import type {
    SessionMinMax,
    WorkerItem,
} from "@mrflark/core/hooks";
import {
    formatElapsed,
} from "@mrflark/core/utils";
import type {
    ComponentChildren,
} from "preact";
import {
    Card,
    CardContent,
    StatusDot,
} from "../mod.tsx";
import {
    getJobTypeLabel,
} from "./types.ts";

type WorkersCardProps = {
    workers: WorkerItem[];
    dispatcherRunning: boolean;
    sessionRanges: Record<string, SessionMinMax>;
    jobTypeLabels?: Record<string, string>;
    maxSlots?: number;
    onWorkerClick?: (jobId: number, jobType: string) => void;
    title?: string;
    compact?: boolean;
};

const DEFAULT_SLOT_COUNT = 4;

export const WorkersCard = ({
    workers,
    dispatcherRunning,
    sessionRanges,
    jobTypeLabels,
    maxSlots = DEFAULT_SLOT_COUNT,
    onWorkerClick,
    title = "Workers",
    compact = false,
}: WorkersCardProps): ComponentChildren => {
    const activeWorkers = workers.filter((worker) => worker.running).length;
    const emptySlotCount = Math.max(0, maxSlots - workers.length);
    const activeRange = sessionRanges.activeWorkers;

    return (
        <Card>
            <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-text-primary">
                        {title}
                    </h3>
                    <span className="flex items-center gap-1.5">
                        <StatusDot
                            status={dispatcherRunning ? "healthy" : "unhealthy"}
                            pulse={dispatcherRunning}
                        />
                        <span
                            className={`text-xs ${
                                dispatcherRunning
                                    ? "text-green-400"
                                    : "text-red-400"
                            }`}
                        >
                            {dispatcherRunning ? "Running" : "Stopped"}
                        </span>
                    </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Active</span>
                    <span className="text-text-primary">
                        {activeWorkers}
                        {activeRange && activeRange.min !== activeRange.max && (
                            <span
                                className="text-text-muted text-xs ml-1"
                                title={`Session range: ${activeRange.min}\u2013${activeRange.max}`}
                            >
                                ({activeRange.min}&ndash;{activeRange.max})
                            </span>
                        )}
                    </span>
                </div>

                {compact
                    ? (
                        <div className="space-y-1">
                            {workers.map((worker) => (
                                <div
                                    key={worker.threadId}
                                    className={`flex items-center gap-1.5 py-1 px-1.5 rounded ${
                                        onWorkerClick
                                            ? "cursor-pointer hover:bg-bg-tertiary"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        onWorkerClick?.(
                                            worker.jobId,
                                            worker.jobType,
                                        )}
                                >
                                    <StatusDot
                                        status={worker.running
                                            ? "healthy"
                                            : "unhealthy"}
                                        pulse={worker.running}
                                    />
                                    <span className="text-xs text-text-primary truncate flex-1">
                                        {getJobTypeLabel(
                                            worker.jobType,
                                            jobTypeLabels,
                                        )}
                                    </span>
                                    <span className="text-xs text-text-muted whitespace-nowrap">
                                        {formatElapsed(worker.startedAt)}{" "}
                                        T{worker.threadId}
                                    </span>
                                </div>
                            ))}
                            {Array.from({
                                length: emptySlotCount,
                            }).map((_, index) => (
                                <div
                                    key={`empty-${index}`}
                                    className="flex items-center gap-1.5 py-1 px-1.5"
                                >
                                    <StatusDot status="unknown" />
                                    <span className="text-xs text-text-muted">
                                        Idle
                                    </span>
                                </div>
                            ))}
                        </div>
                    )
                    : (
                        <div className="grid grid-cols-2 gap-2">
                            {workers.map((worker) => (
                                <div
                                    key={worker.threadId}
                                    className={`bg-bg-tertiary rounded p-2.5 border border-border ${
                                        onWorkerClick
                                            ? "cursor-pointer hover:border-accent"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        onWorkerClick?.(
                                            worker.jobId,
                                            worker.jobType,
                                        )}
                                >
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <StatusDot
                                            status={worker.running
                                                ? "healthy"
                                                : "unhealthy"}
                                            pulse={worker.running}
                                        />
                                        <span className="text-xs font-medium text-text-primary truncate">
                                            {getJobTypeLabel(
                                                worker.jobType,
                                                jobTypeLabels,
                                            )}
                                        </span>
                                    </div>
                                    <div className="text-xs text-text-secondary">
                                        {formatElapsed(worker.startedAt)}
                                    </div>
                                    <div className="text-xs text-text-muted mt-0.5">
                                        T{worker.threadId}{" "}
                                        &middot; Job #{worker.jobId}
                                    </div>
                                </div>
                            ))}
                            {Array.from({
                                length: emptySlotCount,
                            }).map((_, index) => (
                                <div
                                    key={`empty-${index}`}
                                    className="bg-bg-tertiary/50 rounded p-2.5 border border-border/50"
                                >
                                    <div className="text-xs text-text-muted text-center py-2">
                                        Idle
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                {workers.length === 0 && maxSlots === 0 && (
                    <div className="text-sm text-text-secondary text-center py-2">
                        No active workers
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
